/**
 * rect-visibility — pure geometry for the "is this element already in view along
 * one axis?" gate used by ScrollInView (the out-of-view gate). DOM-free: the
 * caller reads the rects via getBoundingClientRect and passes them in, so this is
 * exhaustively unit-testable (same SRP seam as peek-scroll.ts / gesture-axis-lock.ts).
 *
 * @see src-js/model-finder/view/scroll-in-view.ts (the DOM caller)
 * @see docs/speed-optimization/research/2026-06-07 - model-finder-column-plan-and-modal/research/dropdown-bottom-image-autoscroll.md §3.1
 */

export type Axis = 'x' | 'y';

/** The minimal 1-D extent of a box along one axis (start = top|left, end = bottom|right). */
export interface Extent1D {
  readonly start: number;
  readonly end: number;
}

/** Project a DOMRect-like onto one axis. */
export function extentAlong(
  axis: Axis,
  rect: { top: number; bottom: number; left: number; right: number }
): Extent1D {
  return axis === 'y' ? { start: rect.top, end: rect.bottom } : { start: rect.left, end: rect.right };
}

/**
 * Whether `target` is FULLY contained within `viewport` along the axis (with an
 * optional tolerance in px for sub-pixel residue). When the target is TALLER/WIDER
 * than the viewport it can never be "fully visible" — callers treat that as
 * out-of-view and scroll its START into view (see scrollOffsetToReveal).
 */
export function isFullyVisibleAlong(target: Extent1D, viewport: Extent1D, tolerance = 1): boolean {
  return target.start >= viewport.start - tolerance && target.end <= viewport.end + tolerance;
}
